import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

/**
 * Where a client used to live (092). Searchable on the server, so a record under
 * a former address finds the client rather than reading as somebody else.
 * `moved_out_on` is null where the date is not known, and never guessed.
 */
export interface PastAddress {
  id: string;
  street_address: string;
  unit: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  moved_out_on: string | null;
  moved_out_source: string | null;
  source: 'Property discharged' | 'Recorded';
  created_at: string;
  property_disposition: string | null;
  property_discharged_on: string | null;
}

export interface NewPastAddressInput {
  street_address: string;
  unit?: string;
  city?: string;
  state?: string;
  zip?: string;
  moved_out_on?: string;
  moved_out_source?: string;
  notes?: string;
}

export function usePastAddresses(clientId: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'past-addresses'],
    queryFn: () => apiFetch<{ past_addresses: PastAddress[] }>(`/clients/${clientId}/past-addresses`, getToken),
    ...queryDefaults,
  });
}

function useInvalidateClient(clientId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['client', clientId] });
    void queryClient.invalidateQueries({ queryKey: ['clients'] });
  };
}

export function useRecordPastAddress(clientId: string) {
  const { getToken } = useSession();
  const invalidate = useInvalidateClient(clientId);
  return useMutation({
    mutationFn: (body: NewPastAddressInput) =>
      apiFetch<{ id: string }>(`/clients/${clientId}/past-addresses`, getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: invalidate,
  });
}

export function useDatePastAddress(clientId: string) {
  const { getToken } = useSession();
  const invalidate = useInvalidateClient(clientId);
  return useMutation({
    mutationFn: (input: { id: string; moved_out_on: string; moved_out_source: string }) =>
      apiFetch<{ id: string }>(`/past-addresses/${input.id}/date`, getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ moved_out_on: input.moved_out_on, moved_out_source: input.moved_out_source }),
      }),
    onSuccess: invalidate,
  });
}
